import React from 'react';
import { Volume2, CheckCircle2, XCircle, Award, Sparkles, BookOpen } from 'lucide-react';
import { WordValidationResult, StopCategory, getCategorySuggestions } from '../../services/stopGameValidator';
import { speakWord } from '../../utils/stopGameAudio';

interface StopWordReviewProps {
  categories: StopCategory[];
  playerAnswers: Record<string, string>;
  validations: Record<string, WordValidationResult>;
  categoryScores?: Record<string, number>;
  totalRoundScore: number;
  letter: string;
  onNextRound?: () => void;
  nextButtonText?: string;
  isHost?: boolean;
}

export const StopWordReviewModal: React.FC<StopWordReviewProps> = ({
  categories,
  playerAnswers,
  validations,
  categoryScores = {},
  totalRoundScore,
  letter,
  onNextRound,
  nextButtonText = 'Next Round',
  isHost = true
}) => {
  return (
    <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-purple-100 max-w-4xl mx-auto my-6 animate-in fade-in zoom-in-95">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-purple-700 font-bold text-sm mb-3">
          <Sparkles className="w-4 h-4" />
          Round Review • Letter {letter.toUpperCase()}
        </div>
        <h2 className="text-3xl font-extrabold text-gray-900">Word Verification & Learning</h2>
        <p className="text-gray-600 mt-1 max-w-md mx-auto">
          Every word was validated against official English dictionaries. Click the speaker to hear correct pronunciation!
        </p>

        <div className="mt-4 inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-2.5 rounded-2xl shadow-lg shadow-indigo-500/20 font-black text-xl">
          <Award className="w-6 h-6 text-yellow-300" />
          <span>Round Score: +{totalRoundScore} pts</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map(cat => {
          const userWord = playerAnswers[cat.id] || '';
          const validation = validations[cat.id] || { isValid: false, reason: 'No answer submitted' };
          const defaultValidScore = 10;
          const score = categoryScores[cat.id] ?? (validation.isValid ? defaultValidScore : 0);

          return (
            <div
              key={cat.id}
              className={`p-4 rounded-2xl border transition-all ${
                validation.isValid
                  ? 'bg-emerald-50/50 border-emerald-200'
                  : 'bg-red-50/40 border-red-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-xl" role="img" aria-label={cat.name}>
                    {cat.icon}
                  </span>
                  <span className="font-bold text-gray-800 text-sm">{cat.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  {validation.isValid ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      +{score} pts
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-100 px-2.5 py-1 rounded-full">
                      <XCircle className="w-3.5 h-3.5" />
                      0 pts
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span className="text-lg font-extrabold text-gray-900 tracking-tight">
                  {userWord ? (
                    userWord
                  ) : (
                    <span className="text-gray-400 italic text-sm font-normal">None (Not sent)</span>
                  )}
                </span>

                {userWord && validation.isValid && (
                  <button
                    onClick={() => speakWord(validation.correctedWord || userWord)}
                    className="p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-white rounded-lg transition-all shadow-sm border border-indigo-100"
                    title="Listen to English pronunciation"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Typo Warning and Corrected Spelling Display */}
              {validation.isTypo && validation.correctedWord && (
                <div className="mt-2.5 text-xs bg-amber-50 border border-amber-300 text-amber-900 p-2.5 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="font-bold">⚠️ Potential spelling error:</span> Did you mean{' '}
                    <strong className="underline text-amber-950 font-black">{validation.correctedWord}</strong>? (+{score} pts)
                  </div>
                  <button
                    onClick={() => speakWord(validation.correctedWord!)}
                    className="p-1 text-amber-800 hover:bg-amber-100 rounded-lg transition-colors ml-2 flex-shrink-0"
                    title={`Hear pronunciation: ${validation.correctedWord}`}
                  >
                    <Volume2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {validation.phonetic && (
                <div className="mt-1 text-xs font-mono text-purple-700">
                  {validation.phonetic}
                </div>
              )}

              {/* Definition and Meaning */}
              {validation.definition && (
                <div className="mt-2 text-xs text-gray-700 bg-white/80 p-2.5 rounded-xl border border-gray-100 flex items-start gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    {validation.partOfSpeech && (
                      <span className="font-semibold italic text-purple-800 mr-1">
                        [{validation.partOfSpeech}]
                      </span>
                    )}
                    <span>{validation.definition}</span>
                  </div>
                </div>
              )}

              {/* Rejection Reason (e.g. "angry is not a verb") */}
              {!validation.isValid && userWord && (
                <p className="mt-2 text-xs text-red-600 font-semibold bg-red-100/50 px-2.5 py-1.5 rounded-lg border border-red-200/60">
                  {validation.reason || 'Word could not be verified in the English dictionary'}
                </p>
              )}

              {/* Word Suggestions for Unsubmitted OR Wrong Words */}
              {!validation.isValid && (
                <div className="mt-2.5 text-xs bg-purple-50/70 border border-purple-200 p-2.5 rounded-xl">
                  <div className="font-bold text-purple-900 mb-1.5 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                    {userWord ? 'Valid examples you could have used:' : 'Words you could have used:'}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {((validation.suggestedExamples && validation.suggestedExamples.length > 0)
                      ? validation.suggestedExamples
                      : getCategorySuggestions(letter, cat.id)
                    ).map((exampleWord: string) => (
                      <button
                        key={exampleWord}
                        onClick={() => speakWord(exampleWord)}
                        className="inline-flex items-center gap-1 bg-white hover:bg-purple-100 text-purple-800 font-bold px-2 py-0.5 rounded-lg border border-purple-200 transition-colors shadow-2xs text-[11px]"
                        title={`Click to listen to "${exampleWord}"`}
                      >
                        <span>{exampleWord}</span>
                        <Volume2 className="w-3 h-3 text-purple-500" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {onNextRound && isHost && (
        <div className="mt-8 text-center">
          <button
            onClick={onNextRound}
            className="w-full md:w-auto px-8 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-extrabold rounded-2xl shadow-xl shadow-purple-500/25 transition-all transform hover:scale-105"
          >
            {nextButtonText}
          </button>
        </div>
      )}
    </div>
  );
};

export default StopWordReviewModal;
